import type { ArTaskStatusChange } from '@erp/rpc';

interface TimelineStepBase {
  /** Stable and unique within the timeline. */
  key: string;
  /**
   * The ERP's local wall-clock time, no zone; for the starting status, when
   * the order was created, null when unknown.
   */
  at: string | null;
  by: string | null;
}

/** The order reached a status, e.g. "Ship". */
export interface ArTaskStatusStep extends TimelineStepBase {
  kind: 'status';
  status: string;
}

/** Any other field change, e.g. Sale Terms: DELIVERED → DLVD-SHIPPER. */
export interface ArTaskFieldChangeStep extends TimelineStepBase {
  kind: 'change';
  field: string;
  from: string | null;
  to: string | null;
}

export type ArTaskTimelineStep = ArTaskStatusStep | ArTaskFieldChangeStep;

export interface ArTaskTimeline {
  /** Oldest first. */
  steps: ArTaskTimelineStep[];
  /** The order's status now: the last status step's key; null without one. */
  currentKey: string | null;
  /** The invoice date the ERP holds now, e.g. "Oct 16, 2026"; null if unset. */
  invoiceDate: string | null;
}

const STATUS_FIELD = 'status';
const INVOICE_DATE_FIELD = 'invoice date';

/** Famous writes dates as "Oct 16, 2026 00:00:00"; the time is always midnight. */
function invoiceDateOf(value: string | null): string | null {
  return value?.replace(/\s+00:00:00$/, '').trim() || null;
}

type Ordered = { step: ArTaskTimelineStep; seq: number };

function fieldOf(entry: ArTaskStatusChange): string {
  return entry.field.toLowerCase();
}

/**
 * A status step, timed by the change that entered it or, for the status
 * the order started in, by `startedAt`.
 */
function statusStep(
  status: string,
  {
    entry,
    startedAt = null,
  }: { entry?: ArTaskStatusChange; startedAt?: string | null } = {},
): Ordered {
  return {
    step: {
      kind: 'status',
      key: `status:${status}`,
      status,
      at: entry?.at ?? startedAt,
      by: entry?.by ?? null,
    },
    seq: entry?.seq ?? 0,
  };
}

function changeStep(entry: ArTaskStatusChange): Ordered {
  return {
    step: {
      kind: 'change',
      key: `change:${entry.seq}`,
      field: entry.field,
      from: entry.from,
      to: entry.to,
      at: entry.at,
      by: entry.by,
    },
    seq: entry.seq,
  };
}

/**
 * Famous statuses that aren't steps AR follows: Imported is the system's
 * first step for imported orders, and a Quote isn't an order yet.
 */
const HIDDEN_STATUSES = new Set(['imported', 'quote']);

function isShownStatus(status: string | null | undefined): status is string {
  return !!status && !HIDDEN_STATUSES.has(status.toLowerCase());
}

/**
 * One step per status (newest first in), at the latest time the order
 * entered it, plus the status the order started in when it's never
 * re-entered, at when the order was created. Imported and Quote are left
 * out.
 */
function statusSteps(
  changes: readonly ArTaskStatusChange[],
  createdAt: string | null,
): Ordered[] {
  const latest = new Map<string, ArTaskStatusChange>();
  for (const change of changes) {
    if (isShownStatus(change.to) && !latest.has(change.to)) {
      latest.set(change.to, change);
    }
  }
  const steps = [...latest].map(([status, change]) =>
    statusStep(status, { entry: change }),
  );
  const start = changes.at(-1)?.from;
  if (isShownStatus(start) && !latest.has(start)) {
    steps.push(statusStep(start, { startedAt: createdAt }));
  }
  return steps;
}

/**
 * The order's timeline from the change log (newest first), oldest first.
 *
 * Statuses show once each, at the latest time the order entered them:
 * changes saved in the same second have no reliable order, so a
 * Ship -> Order -> Ship flip reads as Order, then Ship.
 *
 * Every other field change is its own step, except the invoice date: it
 * belongs to the Invoiced step, as the date the ERP holds now (the newest
 * change, even one that cleared it).
 *
 * The log has no entry for the order being created, so the status it
 * started in is dated by `createdAt`.
 */
export function buildArTaskTimeline(
  entries: readonly ArTaskStatusChange[],
  createdAt: string | null = null,
): ArTaskTimeline {
  const newestInvoiceDate = entries.find(
    (entry) => fieldOf(entry) === INVOICE_DATE_FIELD,
  );
  const changes = entries.filter(
    (entry) =>
      fieldOf(entry) !== STATUS_FIELD && fieldOf(entry) !== INVOICE_DATE_FIELD,
  );
  const steps = [
    ...changes.map(changeStep),
    ...statusSteps(
      entries.filter((entry) => fieldOf(entry) === STATUS_FIELD),
      createdAt,
    ),
  ]
    .sort(
      (a, b) =>
        (a.step.at ?? '').localeCompare(b.step.at ?? '') || a.seq - b.seq,
    )
    .map(({ step }) => step);
  const current = steps.findLast((step) => step.kind === 'status');
  return {
    steps,
    currentKey: current?.key ?? null,
    invoiceDate: invoiceDateOf(newestInvoiceDate?.to ?? null),
  };
}

/**
 * Whether a step shows before "Show all changes" is turned on: only the
 * order's statuses (the invoice date rides on Invoiced). Every other field
 * change (credit hold, sale terms, carrier and the like) sits behind it.
 */
export function isKeyTimelineStep(step: ArTaskTimelineStep): boolean {
  return step.kind === 'status';
}

export type ArTaskTimelineDot = 'current' | 'status' | 'change';

/** How a step's dot looks: the order's status now, a status, or another change. */
export function arTaskTimelineDot(
  step: ArTaskTimelineStep,
  currentKey: string | null,
): ArTaskTimelineDot {
  if (step.kind === 'change') return 'change';
  return step.key === currentKey ? 'current' : 'status';
}

export type ArTaskHistoryState = 'loading' | 'unavailable' | 'empty' | 'ready';

/** What the Order history section shows for its query. */
export function arTaskHistoryState(query: {
  isPending: boolean;
  data?: { available: boolean; entries: readonly unknown[] };
}): ArTaskHistoryState {
  if (query.isPending) return 'loading';
  if (!query.data?.available) return 'unavailable';
  return query.data.entries.length === 0 ? 'empty' : 'ready';
}

/**
 * What the Order history section shows: the query's state, or, when the
 * loaded history has nothing in the current view, a note instead of an
 * empty strip. `noStatus` is history with changes but no status change yet
 * (e.g. only an invoice date or sale terms), which Show all changes opens.
 */
export type ArTaskHistoryDisplay = ArTaskHistoryState | 'noStatus';

export function arTaskHistoryDisplay(
  state: ArTaskHistoryState,
  allSteps: readonly ArTaskTimelineStep[],
  shownSteps: readonly ArTaskTimelineStep[],
): ArTaskHistoryDisplay {
  if (state !== 'ready' || shownSteps.length > 0) return state;
  return allSteps.length === 0 ? 'empty' : 'noStatus';
}
